import { toggleRail } from '../../../../design/sidebar.js';
import { Link, useLocation } from 'react-router';
import { ChevronsUpDown, KeyRound, LogOut, PanelLeftClose, Search } from 'lucide-react';
import { roleLabel } from '../lib/format';
import { usePopover } from '../components/interactive';
import { useMe } from './auth';
import { NAV, isActive } from './nav';

/**
 * 侧栏（DESIGN.md §2.1）：从上到下是系统切换 · 搜索 · 一级导航 · 当前用户（正式环境不标环境）。
 * 系统切换是入口发布的 <console-switcher>（console/portal/web/public/_console/switcher.js），
 * 位置、顺序、名字在每个后台都一样；账号与权限、我的登录设备在入口。
 */
export default function Sidebar({ portalOrigin, onSearch }: {
  portalOrigin: string;
  onSearch: () => void;
}) {
  const { pathname } = useLocation();
  const me = useMe();
  const menu = usePopover('pop-user', { side: 'top' });
  const name = me.username;

  return (
    <aside className="sb" aria-label="导航">
      <div className="sb-top">
        <console-switcher />
        <button type="button" className="btn btn-quiet btn-icon sb-collapse" aria-label="收起侧栏" title="收起侧栏" aria-expanded="true" onClick={toggleRail}><PanelLeftClose className="ico" aria-hidden="true" /></button>
      </div>
      <div className="sb-search">
        <button type="button" className="searchbtn" aria-label="搜索（⌘K）" onClick={onSearch}>
          <Search className="ico" aria-hidden="true" />
          <span>搜索</span>
          <span className="kbd">⌘K</span>
        </button>
      </div>
      <nav className="sb-scroll" aria-label="问题诊断">
        <ul className="nav">
          {NAV.map((item) => {
            const Icon = item.icon;
            const on = isActive(pathname, item.href);
            return (
              <li key={item.href}>
                <Link className="nav-i" to={item.href} aria-current={on ? 'page' : undefined} title={item.hint}>
                  <Icon className="ico" aria-hidden="true" />
                  <span>{item.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
      <div className="sb-foot">
        {/* 环境只在测试实例上标「测试」；诊断线上只有正式环境一套，不标（DESIGN.md §2.1，汤祺 10-05） */}
        <button type="button" className="userbtn" aria-label={`${name} · ${roleLabel(me.roles)}`} {...menu.trigger}>
          <span className="av" aria-hidden="true">{name.slice(0, 1).toUpperCase()}</span>
          <span className="who">
            <b>{name}</b>
            <span>{roleLabel(me.roles)}</span>
          </span>
          <ChevronsUpDown className="ico" aria-hidden="true" />
        </button>
        <div className="menu" {...menu.popover}>
          <div className="menu-who">
            <b>{name}</b>
            <span>{me.username} · {roleLabel(me.roles)}</span>
          </div>
          <a className="menu-i" role="menuitem" href={`${portalOrigin}/sessions`}>
            <KeyRound className="ico" aria-hidden="true" />我的登录设备
          </a>
          <div className="menu-sep" />
          {/* 先清掉本主机名上的通行证，再到入口作废整次登录：全部后台一起退出。 */}
          <a className="menu-i" role="menuitem" href="/_sso/logout">
            <LogOut className="ico" aria-hidden="true" />退出
          </a>
        </div>
      </div>
    </aside>
  );
}
